import { StorageService } from './storage';

export type MetricTrustState = 'verified' | 'estimated' | 'observed' | 'unverified' | 'not_available';

export interface SupportedProviderInfo {
  id: string;
  name: string;
  status: string;
  description: string;
  badge: string;
  supportedMetrics: string[];
}

export interface DeveloperConnection {
  id: string;
  user_id: string;
  provider: string;
  issuer_id_masked: string;
  key_id_masked: string;
  apple_vendor_number?: string;
  status: string;
  created_at: string;
  last_sync_at?: string;
  last_success_at?: string;
  last_attempted_sync?: string;
  rows_imported?: number;
  sync_status?: string;
  last_error?: string;
}

export interface AppOwnership {
  id: string;
  user_id: string;
  app_id: string;
  app_store_id: number;
  bundle_id?: string;
  developer_connection_id: string;
  verification_status: 'verified' | 'pending' | 'rejected' | 'revoked';
  verified_at?: string;
  created_at: string;
}

export interface DeveloperDailyMetric {
  id: string;
  app_id: string;
  app_store_id: number;
  metric_date: string;
  country_code: string;
  platform: string;
  impressions: number | null;
  product_page_views: number | null;
  first_time_downloads: number | null;
  redownloads: number | null;
  downloads: number | null;
  installations: number | null;
  updates: number | null;
  deletions: number | null;
  sessions: number | null;
  active_devices: number | null;
  crashes: number | null;
  conversion_rate?: number | null;
  source: string;
  provider?: string;
  apple_report_type?: string;
  sync_id?: string;
  verified_at?: string;
  rejected_at?: string | null;
  verification_status?: 'verified' | 'pending' | 'rejected';
  verification_reason?: string;
}

export interface DeveloperFinancialDaily {
  id: string;
  app_id: string;
  app_store_id: number;
  report_date: string;
  country_code: string;
  currency: string;
  app_units: number;
  units: number;
  sales: number;
  proceeds: number;
  iap_units: number;
  iap_sales: number;
  subscription_units: number;
  subscription_sales: number;
  refunds: number;
  source: string;
  verification_status?: 'verified' | 'pending' | 'rejected';
  verification_reason?: string;
}

export interface OwnerSettings {
  id?: string;
  app_store_id: number;
  user_id?: string;
  publish_verified_downloads: boolean;
  publish_verified_rating: boolean;
  publish_verified_install_growth: boolean;
  allow_model_calibration?: boolean;
}

export interface VerifiedAnalyticsResponse {
  verified: boolean;
  appStoreId: number;
  ownership: {
    status: string;
    verifiedAt: string;
    bundleId?: string;
  };
  connection: {
    provider?: string;
    status: string;
    lastSyncAt: string;
    lastSuccessAt: string;
    lastAttemptedSync?: string;
    rowsImported?: number;
    syncStatus?: string;
    providerErrors?: string | null;
  };
  aug28GroundTruth?: {
    metricDate: string;
    firstTimeDownloads: number;
    redownloads: number;
    totalDownloads: number;
    impressions: number;
    productPageViews: number;
    updates: number;
    conversionRate: number;
    sessions: number | null;
    activeDevices: number | null;
    sales: number | null;
    proceeds: number | null;
    source: string;
    calculationMethod?: string;
    trustState?: Record<string, MetricTrustState>;
  } | null;
  summary: {
    firstTimeDownloads: number;
    redownloads: number;
    totalDownloads: number;
    installations: number;
    updates: number;
    deletions: number | null;
    sessions: number | null;
    activeDevices: number | null;
    impressions: number;
    productPageViews: number;
    conversionRate: number;
    calculationMethod?: string;
    crashes: number;
    sales: number | null;
    proceeds: number | null;
    units: number | null;
    subscriptions: number | null;
    refunds: number;
    currency: string;
    monetizationStatus: string;
  };
  estimateComparison?: {
    estimatedDownloads: number;
    estimatedRevenue: number;
    period: string;
    isPrivate: boolean;
    allowCalibration: boolean;
  };
  dailyMetrics: DeveloperDailyMetric[];
  dailyFinancials: DeveloperFinancialDaily[];
  settings: OwnerSettings;
  provenance: string;
  error?: string;
}

export const DeveloperAnalyticsService = {
  getUserId(): string {
    const user = StorageService.getUser();
    if (user?.id) return user.id;
    if (user?.email) return `user_${user.email.replace(/[^a-zA-Z0-9]/g, '_')}`;
    
    let storedId = localStorage.getItem('appstatsview_user_uuid');
    if (!storedId) {
      storedId = `usr_${Math.random().toString(36).substring(2, 11)}_${Date.now()}`;
      localStorage.setItem('appstatsview_user_uuid', storedId);
    }
    return storedId;
  },

  async getSupportedProviders(): Promise<SupportedProviderInfo[]> {
    try {
      const res = await fetch('/api/developer/providers');
      const data = await res.json();
      return data.providers || [];
    } catch {
      return [];
    }
  },

  async connectAppStoreConnect(params: {
    issuerId: string;
    keyId: string;
    privateKey: string;
    vendorNumber?: string;
  }): Promise<{ success: boolean; connection?: DeveloperConnection; error?: string }> {
    const userId = this.getUserId();
    try {
      const res = await fetch('/api/developer/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          issuerId: params.issuerId,
          keyId: params.keyId,
          privateKey: params.privateKey,
          vendorNumber: params.vendorNumber
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to connect App Store Connect account');
      }
      return { success: true, connection: data.connection };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  async getConnections(): Promise<DeveloperConnection[]> {
    const userId = this.getUserId();
    try {
      const res = await fetch(`/api/developer/connections?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      return data.connections || [];
    } catch {
      return [];
    }
  },

  async claimApp(params: {
    appStoreId: number | string;
    bundleId?: string;
    appName?: string;
    connectionId: string;
  }): Promise<{ success: boolean; ownership?: AppOwnership; error?: string }> {
    const userId = this.getUserId();
    try {
      const res = await fetch('/api/developer/claim-app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          connectionId: params.connectionId,
          appStoreId: params.appStoreId,
          bundleId: params.bundleId,
          appName: params.appName
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to claim application');
      }
      return { success: true, ownership: data.ownership };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  async checkOwnership(appStoreId: number | string): Promise<{ isOwner: boolean; ownership?: AppOwnership | null }> {
    const userId = this.getUserId();
    try {
      const res = await fetch(`/api/developer/ownership/${appStoreId}?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      return { isOwner: !!data.isOwner, ownership: data.ownership };
    } catch {
      return { isOwner: false, ownership: null };
    }
  },

  async getVerifiedAnalytics(appStoreId: number | string): Promise<VerifiedAnalyticsResponse | null> {
    const userId = this.getUserId();
    try {
      const res = await fetch(`/api/developer/analytics/${appStoreId}?userId=${encodeURIComponent(userId)}`);
      if (!res.ok) return null;
      const data = await res.json();
      return data;
    } catch {
      return null;
    }
  },

  async syncMetricsNow(appStoreId: number | string, connectionId?: string): Promise<{ success: boolean; syncedRows?: number; error?: string }> {
    const userId = this.getUserId();
    try {
      const res = await fetch(`/api/developer/sync/${appStoreId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, connectionId, force: true })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Sync request failed');
      }
      return { success: true, syncedRows: data.syncedRows };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  async updateSettings(appStoreId: number | string, settings: Partial<OwnerSettings>): Promise<{ success: boolean; settings?: OwnerSettings; error?: string }> {
    const userId = this.getUserId();
    try {
      const res = await fetch(`/api/developer/settings/${appStoreId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, settings })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to update settings');
      }
      return { success: true, settings: data.settings };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }
};
